/**
 * QAITokensPage - Token Purchase Page for MT5 Panel Users
 *
 * Route: /q-ai/tokens
 * Target: MT5 Panel users who click "Buy Tokens" button
 *
 * Flow:
 * 1. MT5 Panel detects low balance → Shows "Buy Tokens" button
 * 2. QBridge opens browser: https://qforgex.com/q-ai/tokens?source=mt5-panel&mt5Account=12345
 * 3. User completes payment on this page
 * 4. Frontend notifies QBridge of payment completion
 * 5. QBridge updates MT5 Panel balance display
 *
 * URL Parameters:
 * - source: 'mt5-panel' | 'web' | 'qbridge' (for analytics)
 * - mt5Account: MT5 account number (for verification)
 * - returnTo: Optional redirect after purchase
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarIcon from '@mui/icons-material/Star';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QAIHeader from '../../Components/QAI/QAIHeader';
import QAILogo from '../../Components/QAI/QAILogo';
import TokenBalance from '../../Components/QAI/TokenBalance';
import { getBalance } from '../../api/qaiApi';
import { notifyPaymentComplete, requestBalanceRefresh } from '../../services/qbridge.service';

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const BRAND = {
  primary: '#27B56D',
  primaryLight: '#2FCB7A',
  primaryDark: '#1E9456',
  background: '#0A0F0D',
  surface: '#111916',
  surfaceLight: '#1A2420',
  text: '#FFFFFF',
  textSecondary: '#B8C4BC',
  textMuted: '#6B7C72',
  border: '#1E2A24',
  borderLight: '#2A3830',
  gold: '#FFD700',
  warning: '#F59E0B'
};

// ═══════════════════════════════════════════════════════════════════════════════
// TOKEN PACKAGES
// ═══════════════════════════════════════════════════════════════════════════════

const TOKEN_PACKAGES = [
  {
    id: 'starter',
    name: 'Starter',
    tokens: 1000,
    price: 9.99,
    pricePerToken: 0.00999,
    icon: LocalOfferIcon,
    popular: false,
    description: 'Perfect for trying out Q.AI'
  },
  {
    id: 'trader',
    name: 'Trader',
    tokens: 5000,
    price: 39.99,
    pricePerToken: 0.00800,
    savings: '20%',
    icon: StarIcon,
    popular: true,
    description: 'Most popular for active traders'
  },
  {
    id: 'pro',
    name: 'Pro',
    tokens: 15000,
    price: 99.99,
    pricePerToken: 0.00667,
    savings: '33%',
    icon: RocketLaunchIcon,
    popular: false,
    description: 'Best value for power users'
  }
];

/**
 * QAITokensPage Component
 */
const QAITokensPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL params from MT5 Panel
  const source = searchParams.get('source') || 'web';
  const mt5Account = searchParams.get('mt5Account');
  const returnTo = searchParams.get('returnTo');

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [userEmail, setUserEmail] = useState(null);

  // Check authentication and load balance on mount
  useEffect(() => {
    const loadUserData = async () => {
      const token = localStorage.getItem('qai_token');
      const user = localStorage.getItem('qai_user');

      if (!token) {
        // Redirect to login with return URL
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        navigate(`/q-ai/login?redirect=${returnUrl}`);
        return;
      }

      if (user) {
        try {
          const userData = JSON.parse(user);
          setUserEmail(userData.email);
        } catch (e) {
          console.warn('Could not parse user data');
        }
      }

      // Load current balance
      try {
        const balanceResult = await getBalance(token);
        if (balanceResult.success) {
          setCurrentBalance(balanceResult.balance);
        }
      } catch (err) {
        console.warn('Could not load balance:', err);
      }

      setIsLoading(false);
    };

    loadUserData();
  }, [navigate]);

  /**
   * Handle package selection
   */
  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setError(null);
  };

  /**
   * Handle purchase - placeholder for actual payment integration
   * In production, this would integrate with Stripe/PayPal/etc.
   */
  const handlePurchase = async () => {
    if (!selectedPackage) return;

    setIsPurchasing(true);
    setError(null);

    try {
      // TODO: Integrate with actual payment gateway (Stripe, PayPal, etc.)
      // For now, this is a placeholder that simulates the flow

      // Simulated payment result
      const paymentResult = {
        success: true,
        tokensAdded: selectedPackage.tokens,
        newBalance: (currentBalance || 0) + selectedPackage.tokens,
        transactionId: `TXN_${Date.now()}`
      };

      // Update local balance
      setCurrentBalance(paymentResult.newBalance);

      // Notify QBridge if user came from MT5 Panel
      if (source === 'mt5-panel') {
        await notifyPaymentComplete(paymentResult);
        await requestBalanceRefresh();
      }

      setPurchaseSuccess(true);

      // Auto-close after success if from MT5 Panel
      if (source === 'mt5-panel') {
        setTimeout(() => {
          // Show message to close tab and return to MT5
          // Browser can't auto-close tabs it didn't open
        }, 3000);
      }

    } catch (err) {
      setError('Payment failed. Please try again.');
      console.error('Purchase error:', err);
    } finally {
      setIsPurchasing(false);
    }
  };

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo);
    } else if (source === 'mt5-panel') {
      // Show instruction to close tab
      navigate('/q-ai/mt5-ready');
    } else {
      navigate('/q-ai/coach');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: BRAND.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress sx={{ color: BRAND.primary }} />
      </Box>
    );
  }

  // Purchase success screen
  if (purchaseSuccess) {
    return (
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: BRAND.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3
      }}>
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              backgroundColor: 'rgba(39, 181, 109, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: BRAND.primary }} />
            </Box>

            <Typography variant="h4" sx={{ color: BRAND.text, fontWeight: 800, mb: 2 }}>
              Purchase Successful!
            </Typography>

            <Typography sx={{ color: BRAND.textSecondary, fontSize: '1.125rem', mb: 3 }}>
              {selectedPackage?.tokens.toLocaleString()} tokens have been added to your account.
            </Typography>

            <Box sx={{
              backgroundColor: BRAND.surface,
              borderRadius: '12px',
              p: 3,
              mb: 4,
              border: `1px solid ${BRAND.border}`
            }}>
              <Typography sx={{ color: BRAND.textMuted, fontSize: '0.875rem', mb: 1 }}>
                New Balance
              </Typography>
              <Typography sx={{ color: BRAND.primary, fontSize: '2.5rem', fontWeight: 800 }}>
                {currentBalance?.toLocaleString() || '—'} tokens
              </Typography>
            </Box>

            {source === 'mt5-panel' ? (
              <Box>
                <Alert
                  severity="success"
                  sx={{
                    mb: 3,
                    backgroundColor: 'rgba(39, 181, 109, 0.1)',
                    color: BRAND.text,
                    '& .MuiAlert-icon': { color: BRAND.primary }
                  }}
                >
                  Your MT5 Panel balance will update automatically. You can close this tab and return to MT5.
                </Alert>

                <Button
                  variant="contained"
                  onClick={() => window.close()}
                  sx={{
                    backgroundColor: BRAND.primary,
                    color: '#000',
                    fontWeight: 700,
                    py: 1.5,
                    px: 4,
                    borderRadius: '10px',
                    '&:hover': { backgroundColor: BRAND.primaryLight }
                  }}
                >
                  Close & Return to MT5
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={() => navigate('/q-ai/coach')}
                sx={{
                  backgroundColor: BRAND.primary,
                  color: '#000',
                  fontWeight: 700,
                  py: 1.5,
                  px: 4,
                  borderRadius: '10px',
                  '&:hover': { backgroundColor: BRAND.primaryLight }
                }}
              >
                Start Chatting with Q.AI
              </Button>
            )}
          </Box>
        </Container>
      </Box>
    );
  }

  // Determine account type from URL source
  const accountType = source === 'mt5-panel' ? 'mt5' : 'both';

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: BRAND.background,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Shared Header */}
      <QAIHeader
        accountType={accountType}
        balance={currentBalance}
        isLowBalance={currentBalance !== null && currentBalance <= 15}
        currentPage="tokens"
        showNavigation={true}
        showQBridgeStatus={source === 'mt5-panel'}
      />

      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 }, flex: 1 }}>
        {/* Back button for context */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              color: BRAND.textSecondary,
              textTransform: 'none',
              '&:hover': { color: BRAND.text, backgroundColor: 'transparent' }
            }}
          >
            {source === 'mt5-panel' ? 'Back to MT5 Setup' : 'Back to Coach'}
          </Button>
        </Box>

        {/* Source indicator for MT5 users */}
        {source === 'mt5-panel' && (
          <Alert
            severity="info"
            sx={{
              mb: 3,
              backgroundColor: 'rgba(39, 181, 109, 0.1)',
              color: BRAND.text,
              border: `1px solid ${BRAND.border}`,
              '& .MuiAlert-icon': { color: BRAND.primary }
            }}
          >
            Purchase tokens here and your MT5 Panel balance will update automatically.
            {mt5Account && ` (MT5 Account: ${mt5Account})`}
          </Alert>
        )}

        {/* Title */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography
            variant="h3"
            sx={{
              color: BRAND.text,
              fontWeight: 800,
              mb: 1.5,
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' }
            }}
          >
            Get More Q.AI Tokens
          </Typography>
          <Typography sx={{ color: BRAND.textSecondary, fontSize: '1.125rem' }}>
            Choose a package that fits your trading needs
          </Typography>
        </Box>

        {/* Error display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Token packages */}
        <Grid container spacing={3} justifyContent="center">
          {TOKEN_PACKAGES.map((pkg) => {
            const IconComponent = pkg.icon;
            const isSelected = selectedPackage?.id === pkg.id;

            return (
              <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                <Card
                  onClick={() => handleSelectPackage(pkg)}
                  sx={{
                    backgroundColor: isSelected ? BRAND.surfaceLight : BRAND.surface,
                    border: `2px solid ${isSelected ? BRAND.primary : BRAND.border}`,
                    borderRadius: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                      borderColor: BRAND.primary,
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 32px rgba(39, 181, 109, 0.15)`
                    }
                  }}
                >
                  {/* Popular badge */}
                  {pkg.popular && (
                    <Chip
                      label="MOST POPULAR"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: BRAND.primary,
                        color: '#000',
                        fontWeight: 700,
                        fontSize: '0.6875rem',
                        letterSpacing: '0.05em'
                      }}
                    />
                  )}

                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    {/* Icon */}
                    <Box sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      backgroundColor: isSelected ? 'rgba(39, 181, 109, 0.2)' : 'rgba(39, 181, 109, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2
                    }}>
                      <IconComponent sx={{ fontSize: 28, color: BRAND.primary }} />
                    </Box>

                    {/* Package name */}
                    <Typography sx={{ color: BRAND.text, fontWeight: 700, fontSize: '1.25rem', mb: 0.5 }}>
                      {pkg.name}
                    </Typography>

                    {/* Description */}
                    <Typography sx={{ color: BRAND.textMuted, fontSize: '0.875rem', mb: 2 }}>
                      {pkg.description}
                    </Typography>

                    {/* Token count */}
                    <Typography sx={{ color: BRAND.primary, fontWeight: 800, fontSize: '2rem', mb: 0.5 }}>
                      {pkg.tokens.toLocaleString()}
                    </Typography>
                    <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.875rem', mb: 2 }}>
                      tokens
                    </Typography>

                    {/* Price */}
                    <Typography sx={{ color: BRAND.text, fontWeight: 800, fontSize: '1.75rem', mb: 0.5 }}>
                      ${pkg.price}
                    </Typography>

                    {/* Savings badge */}
                    {pkg.savings && (
                      <Chip
                        label={`Save ${pkg.savings}`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(245, 158, 11, 0.15)',
                          color: BRAND.warning,
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    )}

                    {/* Price per token */}
                    <Typography sx={{ color: BRAND.textMuted, fontSize: '0.75rem', mt: 1 }}>
                      ${pkg.pricePerToken.toFixed(4)} per token
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Purchase button */}
        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <Button
            variant="contained"
            size="large"
            disabled={!selectedPackage || isPurchasing}
            onClick={handlePurchase}
            sx={{
              backgroundColor: BRAND.primary,
              color: '#000',
              fontWeight: 700,
              fontSize: '1.125rem',
              py: 2,
              px: 6,
              borderRadius: '12px',
              minWidth: 280,
              '&:hover': {
                backgroundColor: BRAND.primaryLight,
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 24px rgba(39, 181, 109, 0.3)`
              },
              '&:disabled': {
                backgroundColor: BRAND.border,
                color: BRAND.textMuted
              }
            }}
          >
            {isPurchasing ? (
              <CircularProgress size={24} sx={{ color: '#000' }} />
            ) : selectedPackage ? (
              `Purchase ${selectedPackage.tokens.toLocaleString()} Tokens - $${selectedPackage.price}`
            ) : (
              'Select a Package'
            )}
          </Button>

          <Typography sx={{ color: BRAND.textMuted, fontSize: '0.8125rem', mt: 2 }}>
            Secure payment powered by Stripe. Tokens never expire.
          </Typography>
        </Box>

        {/* Footer info */}
        <Box sx={{ textAlign: 'center', mt: 6, pb: 4 }}>
          <Typography sx={{ color: BRAND.textMuted, fontSize: '0.8125rem' }}>
            Questions? Contact us at{' '}
            <Box
              component="a"
              href="mailto:info@qforgex.com"
              sx={{ color: BRAND.primary, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              info@qforgex.com
            </Box>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default QAITokensPage;
